import React, { useRef, useEffect, useState } from 'react';
import { Mui } from '@osu/react-ui';
import { VerticalTimelineStep } from '../core';
import { isNoApplicationsScenario } from '../../utils/stepDefinitions';
import NoApplicationsComponent from './NoApplicationsComponent';

/**
 * Enhanced DashboardContent Component for V2
 * Main content area that renders timeline with template-based step rendering
 */
function DashboardContent({ 
  applicationData, 
  timelineSteps = [], 
  stepNavigation = null,
  onStepAction 
}) {
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(0);

  // Use visible steps from step navigation if available, otherwise fall back to all timeline steps
  const stepsToShow = stepNavigation?.visibleSteps || timelineSteps;
  
  // Measure container height after render
  useEffect(() => {
    const measureHeight = () => {
      if (containerRef.current) {
        // to do: revisit sizing
        const height = applicationData?.selfService?.currentStep === "HONORS_DECLINED" ? containerRef.current.scrollHeight - 30 : containerRef.current.scrollHeight;
        setContainerHeight(height);
      }
    };

    // Measure after initial render and on resize
    measureHeight();
    window.addEventListener('resize', measureHeight);
    
    // Also measure after a short delay to account for dynamic content
    const timer = setTimeout(measureHeight, 100);

    return () => {
      window.removeEventListener('resize', measureHeight);
      clearTimeout(timer);
    };
  }, [stepsToShow, applicationData]);

  // Show no applications component if needed
  if (isNoApplicationsScenario(applicationData)) {
    return <NoApplicationsComponent />;
  }

  // Find the last active or completed step for continuous line
  const getLastActiveStepIndex = () => {
    for (let i = stepsToShow.length - 1; i >= 0; i--) {
      const step = stepsToShow[i];
      if (step.status === 'COMPLETE' || step.status === 'IN_PROGRESS' || step.isActive) {
        return i;
      }
    }
    return -1;
  };

  const lastActiveStepIndex = getLastActiveStepIndex();
  const shouldShowTimeline = stepsToShow.length > 0 && lastActiveStepIndex >= 0;

  // Calculate timeline height dynamically based on container height and future steps
  const calculateTimelineHeight = () => {
    if (lastActiveStepIndex < 0 || containerHeight === 0) {
      return "0px";
    }

    let baseHeight = 24; // Initial top offset to center of first icon

    // Add height for each step up to but not including the last active step
    for (let i = 0; i < lastActiveStepIndex; i++) {
      const step = stepsToShow[i];
      baseHeight += step.showCondensed ? 96 : 192;
    }

    // For the last active step, calculate how much space we have
    const lastActiveStep = stepsToShow[lastActiveStepIndex];
    
    if (lastActiveStep.showCondensed) {
      // Condensed steps don't need much extension
      return `${baseHeight + 160}px`;
    }

    // Calculate remaining space after future steps
    const futureStepsCount = stepsToShow.length - lastActiveStepIndex - 1;
    const estimatedFutureStepsHeight = futureStepsCount * 96; // Assume condensed future steps
    const availableHeight = containerHeight - baseHeight - estimatedFutureStepsHeight;
    
    // Use most of the available height but leave some buffer
    const extensionHeight = Math.max(200, availableHeight - 100);
    
    return `${baseHeight + extensionHeight}px`;
  };

  return (
    <Mui.Paper elevation={2} sx={{ p: 4, mb: 4 }} ref={containerRef}>
      <Mui.Box sx={{ position: "relative" }}>
        {/* Timeline Line - Continuous line through active steps */}
        {shouldShowTimeline && (
          <Mui.Box
            sx={{
              position: "absolute",
              left: "24px",
              top: "24px",
              height: calculateTimelineHeight(),
              width: "2px",
              backgroundColor: "divider",
              zIndex: 0,
            }}
          />
        )}

        {/* Timeline Steps */}
        <Mui.Box sx={{ position: "relative", zIndex: 1 }}>
          {stepsToShow.length > 0 ? (
            stepsToShow.map((step, index) => (
              <VerticalTimelineStep
                key={step.id}
                step={step}
                isLast={index === stepsToShow.length - 1}
                onAction={onStepAction}
                applicationData={applicationData}
              />
            ))
          ) : (
            <Mui.Box sx={{ textAlign: 'center', py: 4 }}>
              <Mui.Typography variant="h6" color="text.secondary">
                No timeline steps available
              </Mui.Typography>
              <Mui.Typography variant="body2" color="text.secondary">
                Application data may be incomplete or invalid.
              </Mui.Typography>
            </Mui.Box>
          )}
        </Mui.Box>
      </Mui.Box>
    </Mui.Paper>
  );
}

export default DashboardContent;